<template>
  <div class="bpmn-flow-outer">
    <!-- Тулбар -->
    <div v-if="canEditSchema" class="bpmn-toolbar">
      <button class="toolbar-btn toolbar-btn--primary" @click="openAddNode">
        + Узел
      </button>
      <button class="toolbar-btn toolbar-btn--secondary" @click="openAddEdge">
        + Ребро
      </button>
      <Transition name="save-fade">
        <span v-if="saving" class="toolbar-saving">
          <span class="toolbar-saving__spinner">⟳</span> Сохранение позиций...
        </span>
      </Transition>
      <span class="toolbar-hint" v-if="!selectedItem && !saving">
        Клик — редактировать · Ctrl+клик — выделить несколько · Shift+drag по фону — рамка выделения · Drag из точки узла → создать ребро
      </span>
    </div>

    <div class="bpmn-flow-body">
      <!-- Canvas -->
      <div class="bpmn-flow-canvas-wrap">
        <div v-if="loading" class="bpmn-state-msg">Загрузка схемы...</div>
        <div v-else-if="loadError" class="bpmn-state-msg bpmn-state-msg--error">{{ loadError }}</div>
        <VueFlow
          v-else
          :nodes="flowNodes"
          :edges="flowEdges"
          :node-types="nodeTypes"
          :multi-selection-key-code="['Meta', 'Control']"
          selection-key-code="Shift"
          connection-mode="loose"
          fit-view-on-init
          :nodes-draggable="flowCanvasInteractionEnabled"
          :nodes-connectable="flowCanvasInteractionEnabled"
          :elements-selectable="flowCanvasInteractionEnabled"
          class="bpmn-canvas"
          @node-click="onNodeClick"
          @edge-click="onEdgeClick"
          @node-drag-stop="onNodeDragStop"
          @pane-click="onPaneClick"
          @connect="onConnect"
        >
          <Background />
          <Controls
            :show-interactive="canShowSchemaInteractive"
            @interaction-change="onFlowInteractionChange"
          />
          <MiniMap />
        </VueFlow>
      </div>

      <!-- Боковая панель редактирования (только в не-readonly режиме) -->
      <EditPanel
        v-if="editMode && canEditSchema"
        :mode="editMode"
        :item="editItem"
        :schema-id="schemaId"
        :all-nodes="nodes"
        @close="closePanel"
        @saved="onSaved"
        @deleted="onDeleted"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, markRaw } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { VueFlow, useVueFlow } from '@vue-flow/core'
import { Background } from '@vue-flow/background'
import { Controls } from '@vue-flow/controls'
import { MiniMap } from '@vue-flow/minimap'
import '@vue-flow/core/dist/style.css'
import '@vue-flow/core/dist/theme-default.css'
import '@vue-flow/controls/dist/style.css'
import '@vue-flow/minimap/dist/style.css'

import TaskNode from './nodes/TaskNode.vue'
import EditPanel from './EditPanel.vue'
import { wcheckBpmnAPI } from './services/api'
import { getSchemaEditAccess } from './services/services'

const props = defineProps({
  schemaId: { type: Number, required: true },
  currentNodeIds: { type: Array, default: () => [] },
  completedNodeIds: { type: Array, default: () => [] },
  instanceNodes: { type: Array, default: () => [] },
  readonly: { type: Boolean, default: false },
})

const emit = defineEmits(['nodePositionUpdated', 'fireEvent'])

const { multiSelectionActive } = useVueFlow()

const { data: hasSchemaEditGrant } = useQuery({
  queryKey: ['bpmnSchemaEditAccess'],
  queryFn: getSchemaEditAccess,
  refetchOnWindowFocus: false,
})

/** Редактирование схемы: право M__BPMN_schema_edit с бэка; при readonly с родителя — всегда выкл. */
const canEditSchema = computed(
  () => !props.readonly && hasSchemaEditGrant.value === true,
)

/** Кнопка «интерактив/замок» в Controls — только при праве редактирования (по умолчанию права нет). */
const canShowSchemaInteractive = computed(() => hasSchemaEditGrant.value === true)

/** Локальный режим доски: по умолчанию «замок» (false), включается кнопкой Controls при наличии права. */
const flowCanvasInteractive = ref(false)

/** Drag/connect/select на канве: по умолчанию выкл., включается кнопкой Controls (иконка совпадает с внутренним isInteractive). */
const flowCanvasInteractionEnabled = computed(
  () => canEditSchema.value && flowCanvasInteractive.value,
)

function onFlowInteractionChange(active) {
  flowCanvasInteractive.value = active
}

watch(canEditSchema, (allowed) => {
  if (!allowed) flowCanvasInteractive.value = false
})

const nodeTypes = { taskNode: markRaw(TaskNode) }

const nodes = ref([])
const edges = ref([])
const loading = ref(false)
const loadError = ref(null)
const saving = ref(false)

// Редактирование
const editMode = ref(null)   // 'node' | 'edge'
const editItem = ref(null)
const selectedItem = ref(null)

async function loadSchema() {
  loading.value = true
  loadError.value = null
  try {
    const [nodesRes, edgesRes] = await Promise.all([
      wcheckBpmnAPI.getNodes(props.schemaId),
      wcheckBpmnAPI.getEdges(props.schemaId),
    ])
    nodes.value = nodesRes?.data || []
    edges.value = edgesRes?.data || []
  } catch (e) {
    loadError.value = e?.response?.data?.error || e?.message || 'Ошибка загрузки схемы'
  } finally {
    loading.value = false
  }
}

function closePanel() {
  editMode.value = null
  editItem.value = null
  selectedItem.value = null
}

watch(() => props.schemaId, () => {
  flowCanvasInteractive.value = false
  closePanel()
  loadSchema()
}, { immediate: true })

const flowNodes = computed(() =>
  nodes.value.map((n) => {
    const inode = props.instanceNodes.find((i) => i.bpmn_node_id === n.bpmn_node_id) || null
    return {
      id: String(n.bpmn_node_id),
      type: 'taskNode',
      position: { x: n.pos_x, y: n.pos_y },
      data: {
        ...n,
        isCurrent: props.currentNodeIds.length > 0 && props.currentNodeIds.includes(n.bpmn_node_id),
        isCompleted: props.completedNodeIds.length > 0 && props.completedNodeIds.includes(n.bpmn_node_id),
        isSelected: selectedItem.value?.bpmn_node_id === n.bpmn_node_id,
        instanceNode: inode,
        readonly: props.readonly,
        onFireEvent: inode && !props.readonly ? (node) => emit('fireEvent', node) : null,
      },
    }
  })
)

const flowEdges = computed(() =>
  edges.value.map((e) => {
    const isSelected = selectedItem.value?.bpmn_edge_id === e.bpmn_edge_id
    return {
      id: String(e.bpmn_edge_id),
      source: String(e.source_node_id),
      target: String(e.target_node_id),
      sourceHandle: e.source_handle || 'right',
      targetHandle: e.target_handle || 'left',
      label: e.edge_label || '',
      animated: isSelected,
      style: { stroke: isSelected ? '#f57c00' : '#546e7a', strokeWidth: isSelected ? 2.5 : 1.5 },
      labelStyle: { fontSize: 10, fill: '#546e7a' },
      markerEnd: { type: 'arrowclosed', color: isSelected ? '#f57c00' : '#546e7a' },
    }
  })
)

function onNodeClick({ node }) {
  if (!canEditSchema.value || !flowCanvasInteractionEnabled.value) return
  // При активном мульти-выделении (Ctrl/Cmd зажат) — не открываем панель редактирования
  if (multiSelectionActive.value) return
  const found = nodes.value.find((n) => String(n.bpmn_node_id) === node.id)
  if (!found) return
  selectedItem.value = found
  editMode.value = 'node'
  editItem.value = { ...found }
}

function onEdgeClick({ edge }) {
  if (!canEditSchema.value || !flowCanvasInteractionEnabled.value) return
  const found = edges.value.find((e) => String(e.bpmn_edge_id) === edge.id)
  if (!found) return
  selectedItem.value = found
  editMode.value = 'edge'
  editItem.value = { ...found }
}

function onPaneClick() {
  closePanel()
}

function openAddNode() {
  selectedItem.value = null
  editMode.value = 'node'
  editItem.value = {
    node_label: '',
    node_type: 'task',
    node_code: '',
    assign_type_code: null,
    task_type_code: null,
    auto_action_code: null,
  }
}

function openAddEdge(sourceNodeId = null, targetNodeId = null, sourceHandle = 'right', targetHandle = 'left') {
  selectedItem.value = null
  editMode.value = 'edge'
  editItem.value = {
    edge_label: '',
    trigger_event: null,
    trigger_doc_type_code: null,
    trigger_assign_type_code: null,
    trigger_task_type_code: null,
    source_node_id: sourceNodeId,
    target_node_id: targetNodeId,
    source_handle: sourceHandle,
    target_handle: targetHandle,
  }
}

function onConnect({ source, target, sourceHandle, targetHandle }) {
  if (!canEditSchema.value || !flowCanvasInteractionEnabled.value) return
  openAddEdge(parseInt(source), parseInt(target), sourceHandle || 'right', targetHandle || 'left')
}

async function onSaved(payload) {
  closePanel()
  if (payload?.isNew) {
    // Новая запись — нужен ID с сервера, делаем полный reload
    await loadSchema()
    return
  }
  // Точечное обновление — без перезагрузки, без мигания
  if (payload?.type === 'node') {
    const idx = nodes.value.findIndex((n) => n.bpmn_node_id === payload.data.bpmn_node_id)
    if (idx !== -1) nodes.value[idx] = { ...nodes.value[idx], ...payload.data }
  } else if (payload?.type === 'edge') {
    const idx = edges.value.findIndex((e) => e.bpmn_edge_id === payload.data.bpmn_edge_id)
    if (idx !== -1) edges.value[idx] = { ...edges.value[idx], ...payload.data }
  }
}

function onDeleted(payload) {
  closePanel()
  if (payload?.type === 'node') {
    nodes.value = nodes.value.filter((n) => n.bpmn_node_id !== payload.id)
    // Удаляем также все рёбра, связанные с узлом
    edges.value = edges.value.filter(
      (e) => e.source_node_id !== payload.id && e.target_node_id !== payload.id,
    )
  } else if (payload?.type === 'edge') {
    edges.value = edges.value.filter((e) => e.bpmn_edge_id !== payload.id)
  }
}

async function onNodeDragStop({ nodes: draggedNodes }) {
  if (!flowCanvasInteractionEnabled.value) return
  saving.value = true
  try {
    await Promise.all(
      draggedNodes.map((n) =>
        wcheckBpmnAPI.updateNodePosition(parseInt(n.id), n.position.x, n.position.y)
      )
    )
    emit('nodePositionUpdated')
  } catch (e) {
    console.error('Ошибка сохранения позиций узлов:', e)
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.bpmn-flow-outer {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.bpmn-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.toolbar-btn {
  padding: 6px 14px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
}

.toolbar-btn--primary {
  background: #1976d2;
  color: #fff;
}
.toolbar-btn--primary:hover { background: #1565c0; }

.toolbar-btn--secondary {
  background: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #a5d6a7;
}
.toolbar-btn--secondary:hover { background: #c8e6c9; }

.toolbar-hint {
  font-size: 12px;
  color: #90a4ae;
}

.bpmn-flow-body {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.bpmn-flow-canvas-wrap {
  flex: 1;
  min-width: 0;
  height: 600px;
  position: relative;
}

.bpmn-canvas {
  width: 100%;
  height: 100%;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.bpmn-state-msg {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #757575;
  font-size: 14px;
}

.bpmn-state-msg--error {
  color: #c62828;
}

.toolbar-saving {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #1976d2;
  font-weight: 600;
  background: #e3f2fd;
  padding: 4px 10px;
  border-radius: 20px;
  border: 1px solid #90caf9;
}
.toolbar-saving__spinner {
  display: inline-block;
  animation: spin .7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.save-fade-enter-active, .save-fade-leave-active { transition: opacity .2s, transform .2s; }
.save-fade-enter-from, .save-fade-leave-to { opacity: 0; transform: translateY(-4px); }
</style>
